import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { Box, Button, Grid, IconButton } from '@mui/material';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable';
import OperationModeEditor, { EditorRef } from './components/operationModes/OperationModeEditor';
import { useTranslation } from 'react-i18next';
import type { OperationMode } from '@/services';
import { DeleteConfirmationDialogRequireInput, NewPageTitleBar } from '@/components';
import { getMenuData } from '@core/data/menuData.ts';
import { operationModeService } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications.ts';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { DateTime } from 'luxon';
import { useApiStore } from '@core/store';
import {
    defaultOperationModes
} from '@features/administration/components/operationModesSchedules/scheduleDisplay.ts';
import { useUnsavedChangesGuard } from '@core/hooks/useUnsavedChangesGuard.ts';
import { useRegisterDirtyGuard } from '@core/hooks/useRegisterDirtyGuard.ts';
import StatusTag from '@/components/shared/Tags.tsx';
import { DeleteConfirmationDialogRef } from '@/components/dialog/DeleteConfirmationDialogRequireInput.tsx';
import { useModeTagState } from '@core/hooks/useModeTagState.ts';

const OperationModes: FCSecurePage = () => {
    const { t } = useTranslation();
    const { notifySuccess, notifyErrorResponse, notifyError } = useNotifications();
    const [selectedMode, setSelectedMode] = useState<OperationMode | null>(null);
    const [loading, setLoading] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<OperationMode | null>(null);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const maxOperationModes = 5;
    const { operationModes, loadOperationModes } = useApiStore();

    const editorRef = useRef<EditorRef>(null);
    const discardedRef = useRef(false);
    const isDirty = useCallback(() => !discardedRef.current && !!editorRef.current?.isDirty, []);
    const editorSectionRef = useRef<HTMLDivElement>(null);
    const selectShopSectionRef = useRef<HTMLDivElement>(null);

    const deleteDialogRef = useRef<DeleteConfirmationDialogRef>(null);

    const getModeTagState = useModeTagState();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            await loadOperationModes();
            setLastUpdate(DateTime.now());
        } catch (error) {
            console.error(error);
            notifyErrorResponse(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useRegisterRefresh(load, loading);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (selectedMode) {
            discardedRef.current = false;
            editorSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [selectedMode]);

    useEffect(() => {
        setSelectedMode((prev) => {
            if (!prev || !defaultOperationModes.includes(prev.name)) return prev;
            if (editorRef?.current?.isDirty) return prev;

            const currentMode = operationModes.find((mode) => mode.name === prev.name);
            if (currentMode && !currentMode.description) {
                return { ...prev, description: t(`descriptions.${prev.name}`) };
            }

            return prev;
        });
    }, [t]);

    const openEditor = useCallback((item?: OperationMode) => {
        trySelectTemplate(item || { id: '', name: '', description: '' });
    }, []);

    const closeEditor = useCallback(() => {
        discardedRef.current = true;
        setSelectedMode(null);
        selectShopSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const discard = useCallback(() => {
        closeEditor();
    }, []);

    const save = useCallback(async () => {
        const saved = await editorRef.current?.save();
        if (saved) {
            closeEditor();
        }
        return !!saved;
    }, [closeEditor]);

    const { guard } = useUnsavedChangesGuard({ isDirty, save });
    useRegisterDirtyGuard({ checkDirty: isDirty, save, discard });

    const trySelectTemplate = useCallback(
        (next: OperationMode) => {
            guard(() => {
                discardedRef.current = false;
                const description =
                    !next.description && defaultOperationModes.includes(next.name)
                        ? t(`descriptions.${next.name}`)
                        : next.description;
                setSelectedMode({ ...next, description });
            });
        },
        [guard]
    );

    const saveItem = useCallback(async () => {
        closeEditor();
        await load();
    }, []);

    const askDelete = useCallback((item: OperationMode) => {
        console.log('call delete', item);
        setItemToDelete(item);
        deleteDialogRef.current?.openDialog();
    }, []);

    const deleteItem = useCallback(async () => {
        if (!itemToDelete) return;

        setLoading(true);
        closeEditor();
        try {
            await operationModeService.deleteOperationMode(itemToDelete.id);
            notifySuccess(t('deletedOperationMode', { mode: itemToDelete.name }));
            await load();
        } catch (error: any) {
            if (error?.message === 'operationModeInUse' && Array.isArray(error.properties)) {
                const usedByList = error.properties.map((x: string) => `• ${x}`).join('\n');
                const msg = `${t('errors.operationModeInUse')}\n${usedByList}`;
                notifyError(msg);
            } else {
                notifyErrorResponse(error);
            }
        } finally {
            setItemToDelete(null);
            setLoading(false);
        }
    }, [itemToDelete]);

    const handleCancelDelete = useCallback(() => {
        setItemToDelete(null);
    }, []);

    const handleCancelEditor = useCallback(() => {
        guard(() => {
            closeEditor();
        });
    }, [guard, closeEditor]);

    const columns: DataTableColumn<OperationMode>[] = useMemo(
        () => [
            {
                text: t('operationMode'),
                value: 'name',
                width: 200,
                render: (row) => <StatusTag state={getModeTagState(row.name)} />
            },
            {
                text: t('description'),
                value: 'description',
                width: 400,
                render: (row) => row.description || t(`descriptions.${row.name}`)
            },
            {
                text: t('actions'),
                value: 'actions',
                width: 100,
                align: 'left',
                isAction: true,
                sortable: false,
                render: (row) => (
                    <>
                        <IconButton size="small" onClick={() => openEditor(row)}>
                            <i className="bi bi-pencil" />
                        </IconButton>
                        {!defaultOperationModes.includes(row.name) && (
                            <IconButton size="small" onClick={() => askDelete(row)}>
                                <i className="bi bi-trash3" />
                            </IconButton>
                        )}
                    </>
                )
            }
        ],
        [t, operationModes]
    );

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="bi bi-calendar4-range" />}
                title={t('manageOperationModes')}
                menus={getMenuData()}
                iconSize="20px"
                currentRoute={{ path: '/manage-organisation/operationModes' }}
                t={t}
                lastUpdate={lastUpdate}
            />

            <Box sx={{ width: '100%', px: 4, pb: 1, '@media (max-width: 480px)': { pt: 3 } }}>
                <Button
                    variant="text"
                    onClick={() => openEditor()}
                    disabled={loading || maxOperationModes <= operationModes.length}
                    className="pagetitlebar-action-btn pagetitlebar-action-btn--default"
                >
                    <Box className="pagetitlebar-action-btn-icon">
                        <i
                            className="bi bi-plus-lg"
                            style={{ fontSize: '16px', color: 'inherit', lineHeight: 1, paddingRight: 6 }}
                        />
                    </Box>
                    <span>{t('addOperationMode')}</span>
                </Button>
            </Box>

            <Box ref={selectShopSectionRef} sx={{ width: '100%', px: 3, mt: 2, pb: 4 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <NewWcnTable
                            columns={columns}
                            data={operationModes}
                            loading={loading}
                            noDataMessage={t('noData')}
                            size="small"
                        />
                    </Grid>

                    {selectedMode && (
                        <Grid item xs={12} ref={editorSectionRef}>
                            <OperationModeEditor
                                ref={editorRef}
                                item={selectedMode}
                                onSave={saveItem}
                                onDelete={askDelete}
                                onCancel={handleCancelEditor}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <DeleteConfirmationDialogRequireInput
                dialogRef={deleteDialogRef}
                heading={t('deleteHeading')}
                message={t('deleteMessage', { item: t('operationMode') })}
                cancelText={t('keepOperationMode')}
                onConfirm={deleteItem}
                expectedInputText={itemToDelete?.name || ''}
                onClose={handleCancelDelete}
            />
        </Box>
    );
};

export default OperationModes;
